import React, { useState } from "react";
import { Link } from "react-router-dom";
import notifications from "../../../../common/Components/Notifications/Notification";
import { confirmTransfer, disputeTransfer, rejectTransfer, reviseTransfer } from "../../../../services/cashBookApi";
import { T, useKT } from "../../i18n/kt";
import { kp } from "../../karkhanaPaths";
import { newRequestId } from "../../rounds/entryQueue";
import { rupees } from "../../rates/rateModel";
import { fmtTime, num, refusalText } from "../money";
import type { TransferRejectCode, TransferView } from "../types";
import Keypad from "../ui/Keypad";
import "../cashbook.scss";
import "./transfer.scss";

/**
 * Contract used by SupervisorCashHome (FE-1): the supervisor's transfer cards above the sent list.
 * `incoming` = handovers to this supervisor still to answer (B3) or refused by him and not yet corrected (B5); `outgoing` = his own, in transit (B2)
 * or rejected and not yet corrected (B6). `onChanged` reloads the day after confirm/reject/revise/dispute.
 * `readOnly` (owner's HolderDay) shows the cards without buttons.
 */
export type TransferCardsProps = {
  incoming: TransferView[];
  outgoing: TransferView[];
  onChanged: () => void;
  readOnly?: boolean;
};

export const REJECT_REASONS: { code: TransferRejectCode; label: string }[] = [
  { code: "GOT_LESS", label: "Got less" },
  { code: "NOT_RECEIVED", label: "Didn't get it" },
  { code: "OTHER", label: "Something else" },
];

/** Can "Tell Ramesh" be sent? A reason; for "Got less" an amount below what was sent; words for "Something else". */
export const rejectReady = (code: TransferRejectCode | null, received: string, note: string, amount: string): boolean => {
  if (!code) return false;
  if (code === "GOT_LESS") return received !== "" && num(received) >= 0 && num(received) < num(amount);
  if (code === "OTHER") return note.trim().length > 0;
  return true;
};

/** What the giver reads after a "no": the receiver's words, else the reason in plain words. */
export const rejectNoteText = (x: Pick<TransferView, "rejectNote" | "rejectReasonCode" | "receivedAmount">, t: T): string => {
  if (x.rejectNote && x.rejectNote.trim()) return x.rejectNote.trim();
  if (x.rejectReasonCode === "GOT_LESS" && x.receivedAmount !== null) return t("Only {{amount}} came", { amount: rupees(x.receivedAmount) });
  if (x.rejectReasonCode === "NOT_RECEIVED") return t("Didn't get it");
  return t("Something else");
};

/** The receiver's own words after saying no (B5). */
export const afterRejectText = (x: Pick<TransferView, "rejectReasonCode" | "receivedAmount" | "amount" | "from">, t: T): string =>
  x.rejectReasonCode === "GOT_LESS" && x.receivedAmount !== null
    ? t("You said: only {{amount}} came. {{full}} was not added to you. {{name}} will correct it. Keep this money apart till then.", {
        amount: rupees(x.receivedAmount),
        full: rupees(x.amount),
        name: x.from.name,
      })
    : t("You said it is wrong. {{full}} was not added to you. {{name}} will correct it.", { full: rupees(x.amount), name: x.from.name });

/** Giver's rejected handovers that still wait for him (not corrected yet). */
export const waitingOnGiver = (x: TransferView) => x.state === "REJECTED" && !x.revisedById;

const offline = (t: T) => notifications({ title: t("No connection"), message: t("No network. Nothing changed; tap again."), type: "warning" });

/** B5: "What's wrong?" for the receiver. */
export const RejectForm = ({ x, onDone, onCancel }: { x: TransferView; onDone: (updated: TransferView) => void; onCancel?: () => void }) => {
  const { t } = useKT();
  const [code, setCode] = useState<TransferRejectCode | null>(null);
  const [received, setReceived] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = rejectReady(code, received, note, x.amount);
  const send = async () => {
    if (!ready || !code) return;
    setBusy(true);
    setError(null);
    try {
      const res = await rejectTransfer(x.id, {
        version: x.version,
        reasonCode: code,
        note: note.trim() || null,
        receivedAmount: code === "GOT_LESS" ? received : null,
      });
      if (!res?.success || !res.data) {
        setError(refusalText(res, t));
        return;
      }
      onDone(res.data.transfer);
    } catch {
      setError(t("No network. Nothing changed; tap again."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="cb-card">
      <p>
        <strong>{t("What's wrong?")}</strong>
      </p>
      <div className="cb-chips" role="group" aria-label={t("What's wrong?")}>
        {REJECT_REASONS.map((r) => (
          <button key={r.code} type="button" className={`cb-chip${code === r.code ? " is-on" : ""}`} aria-pressed={code === r.code} onClick={() => setCode(r.code)}>
            {t(r.label)}
          </button>
        ))}
      </div>
      {code === "GOT_LESS" ? (
        <>
          <Keypad value={received} onChange={setReceived} label={t("How much did you get?")} id={`cb-got-${x.id}`} />
          {received !== "" && num(received) >= num(x.amount) ? <p className="cb-muted">{t("That is not less than {{amount}}.", { amount: rupees(x.amount) })}</p> : null}
        </>
      ) : null}
      {code ? (
        <label className="cb-field">
          <span className="cb-field__label">{code === "OTHER" ? t("What happened") : t("For what work? (optional)")}</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} />
        </label>
      ) : null}
      <button type="button" className="cb-btn is-primary" disabled={!ready || busy} onClick={send}>
        {busy ? t("Sending…") : t("Tell {{name}}", { name: x.from.name })}
      </button>
      {onCancel ? (
        <button type="button" className="cb-link" onClick={onCancel}>
          {t("Back")}
        </button>
      ) : null}
      {error ? (
        <p className="cb-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};

/** B3: a handover to me, still to answer. */
export const IncomingCard = ({ x, onChanged, readOnly }: { x: TransferView; onChanged: () => void; readOnly?: boolean }) => {
  const { t } = useKT();
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [said, setSaid] = useState<TransferView | null>(null);
  const confirm = async () => {
    setBusy(true);
    try {
      const res = await confirmTransfer(x.id, { version: x.version });
      if (!res?.success || !res.data) {
        notifications({ title: t("Not saved"), message: refusalText(res, t), type: "danger" });
        if (res?.code === "TRANSFER_DECIDED" || res?.code === "CHANGED_MEANWHILE") onChanged();
        return;
      }
      const total = res.data.cashInHand;
      notifications({
        title: t("{{amount}} added · Cash in hand {{total}}", { amount: rupees(x.amount), total: rupees(total) }),
        message: t("Before {{before}} + received {{amount}}", { before: rupees(num(total) - num(x.amount)), amount: rupees(x.amount) }),
        type: "success",
      });
      onChanged();
    } catch {
      offline(t);
    } finally {
      setBusy(false);
    }
  };
  if (said)
    return (
      <div className="cb-card is-warn" role="status">
        <p>{afterRejectText(said, t)}</p>
      </div>
    );
  return (
    <div className="cb-card cb-transfer">
      <p>
        <strong>{t("{{name}} gave you {{amount}}", { name: x.from.name, amount: rupees(x.amount) })}</strong> · {fmtTime(x.createdAt)}
        {x.note ? ` · "${x.note}"` : ""}
      </p>
      <p className="cb-muted">{t('Count, then tap. It adds to your cash in hand only when you tap "Received".')}</p>
      {readOnly ? null : rejecting ? (
        <RejectForm
          x={x}
          onCancel={() => setRejecting(false)}
          onDone={(updated) => {
            setSaid(updated);
            onChanged();
          }}
        />
      ) : (
        <div className="cb-row-btns is-two">
          <button type="button" className="cb-btn is-primary" disabled={busy} onClick={confirm}>
            {busy ? t("Saving…") : t("Received")}
          </button>
          <button type="button" className="cb-btn" disabled={busy} onClick={() => setRejecting(true)}>
            {t("Something's wrong")}
          </button>
        </div>
      )}
    </div>
  );
};

/** B6: the receiver said no; the giver writes again what he really gave, or tells the owner. */
export const GiverAfterReject = ({ x, onChanged, readOnly }: { x: TransferView; onChanged: () => void; readOnly?: boolean }) => {
  const { t } = useKT();
  const [revising, setRevising] = useState(false);
  const [amount, setAmount] = useState(x.receivedAmount !== null && num(x.receivedAmount) > 0 ? String(num(x.receivedAmount)) : "");
  const [requestId, setRequestId] = useState(newRequestId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const revise = async () => {
    if (num(amount) <= 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await reviseTransfer(x.id, { amount, note: x.note, clientRequestId: requestId });
      if (!res?.success || !res.data) {
        setError(refusalText(res, t));
        if (res?.code === "ALREADY_REVISED") onChanged();
        return;
      }
      setRequestId(newRequestId());
      notifications({ title: t("Give {{amount}} to {{name}}", { amount: rupees(amount), name: x.to.name }), message: t("Cash in hand {{amount}}", { amount: rupees(res.data.cashInHand) }), type: "success" });
      onChanged();
    } catch {
      setError(t("No network. Nothing changed; tap again."));
    } finally {
      setBusy(false);
    }
  };

  const dispute = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await disputeTransfer(x.id, { note: t("I gave {{amount}}", { amount: rupees(x.amount) }) });
      if (!res?.success) {
        setError(refusalText(res, t));
        return;
      }
      onChanged();
    } catch {
      setError(t("No network. Nothing changed; tap again."));
    } finally {
      setBusy(false);
    }
  };

  const hasReceived = x.receivedAmount !== null && num(x.receivedAmount) > 0;
  return (
    <div className="cb-card is-warn">
      <p>
        <strong>{t('{{name}} said no: "{{note}}"', { name: x.to.name, note: rejectNoteText(x, t) })}</strong>
      </p>
      <p>{t("In the app {{amount}} is back with you. Write again what you really gave.", { amount: rupees(x.amount) })}</p>
      {x.disputed ? <p className="cb-state is-sent">{t("Owner told")} ✓</p> : null}
      {readOnly ? null : revising ? (
        <>
          <Keypad value={amount} onChange={setAmount} label={t("Amount")} id={`cb-revise-${x.id}`} />
          <button type="button" className="cb-btn is-primary" disabled={busy || num(amount) <= 0} onClick={revise}>
            {busy ? t("Saving…") : t("Give {{amount}} to {{name}}", { amount: rupees(amount || 0), name: x.to.name })}
          </button>
          <button type="button" className="cb-link" onClick={() => setRevising(false)}>
            {t("Back")}
          </button>
        </>
      ) : (
        <>
          <button type="button" className="cb-btn is-primary" disabled={busy} onClick={() => setRevising(true)}>
            {hasReceived ? t("Give {{amount}} again", { amount: rupees(x.receivedAmount) }) : t("Give again")}
          </button>
          {!x.disputed ? (
            <button type="button" className="cb-btn" disabled={busy} onClick={dispute}>
              {t("I gave {{amount}} - tell the owner", { amount: rupees(x.amount) })}
            </button>
          ) : null}
        </>
      )}
      {error ? (
        <p className="cb-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};

/** B2: given by me, waiting for the receiver. */
export const OutgoingWaiting = ({ x }: { x: TransferView }) => {
  const { t } = useKT();
  return (
    <Link to={kp.cashTransfer(x.id)} className="cb-card cb-transfer is-link">
      <p>{t('Waiting for {{name}} to tap "Received" · {{amount}} not counted for anyone yet', { name: x.to.name, amount: rupees(x.amount) })}</p>
    </Link>
  );
};

/**
 * Handovers between supervisors on the नकद home (V1_SPEC 11.5 B2, B3, B6). Incoming ones are shown
 * apart and add to cash in hand only on "Received" (D18).
 */
const TransferCards = ({ incoming, outgoing, onChanged, readOnly = false }: TransferCardsProps) => {
  const { t } = useKT();
  const inc = incoming.filter((x) => x.state === "IN_TRANSIT");
  // The receiver's own "no", until the giver corrects it: the reload after Reject must not drop the
  // "keep this money apart" note (B5), so it comes from the day, not from the card's local state.
  const saidNo = incoming.filter(waitingOnGiver);
  const waiting = outgoing.filter((x) => x.state === "IN_TRANSIT");
  const rejected = outgoing.filter(waitingOnGiver);
  if (!inc.length && !saidNo.length && !waiting.length && !rejected.length) return null;
  return (
    <div className="cb-transfers">
      {inc.map((x) => (
        <IncomingCard key={x.id} x={x} onChanged={onChanged} readOnly={readOnly} />
      ))}
      {saidNo.map((x) => (
        <div key={x.id} className="cb-card is-warn" role="status">
          <p>{afterRejectText(x, t)}</p>
        </div>
      ))}
      {rejected.map((x) => (
        <GiverAfterReject key={x.id} x={x} onChanged={onChanged} readOnly={readOnly} />
      ))}
      {waiting.map((x) => (
        <OutgoingWaiting key={x.id} x={x} />
      ))}
    </div>
  );
};

export default TransferCards;
